import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniBottomSheet } from '@/design-system/components/bottomSheet/UniBottomSheet';
import { UniButton } from '@/design-system/components/buttons';
import { getObjectScreenNameFromPath, popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { UniTabHeader } from '@/design-system/components/tabs';
import { useBottomsheet } from '@/devtools/screens_deprecated/useBottomsheet.hook';
import BottomSheet from '@gorhom/bottom-sheet';
import { useFocusEffect, useIsFocused, useNavigation } from '@react-navigation/native';
import { ScrollView, SxProp, View } from 'dripsy';
import * as DocumentPicker from 'expo-document-picker';
import _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStore } from 'store/store';
import BillPaymentApp from '../..';
import { DatePickerType } from '../../enum';
import { useBillPaymentForm } from '../../hooks/useBillPaymentForm.hook';
import { PAYMENT_TYPE, QuoteDataRequest, QuoteResponse, useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentConfirmProp, BillPaymentStackRoutes, DetailFormRequest } from '../../payment.navigator';
import { mapUrlLink, onGoBack } from '../../utils/utils';
import { ConfirmFormBillPayment } from '../components/ConfirmFormBillPayment.bottomsheet';
import { ConfirmGoogleOtp } from '../components/ConfirmGoogleOtp.bottomsheet';
import { ConfirmSmsOtp } from '../components/ConfirmSmsOtp.bottomsheet';
import { DateFilterPopup } from '../components/DateFilterPopup';
import { HeaderBackgroundImageComponent } from '../components/HeaderBackgroundImageComponent';
import { ScheduleComponentMobileComponent } from '../components/scheduleComponent/ScheduleComponentMobile.component';

export interface BillPaymentFormScheduleMobileProps {
  detailForm?: DetailFormRequest;
  request: QuoteDataRequest;
  response: QuoteResponse;
  documentSelected?: DocumentPicker.DocumentResult;
  paymentType: PAYMENT_TYPE;
}

export const BillPaymentFormScheduleMobileComponent: React.FC<BillPaymentFormScheduleMobileProps> = ({
  detailForm: detailFormProp,
  request,
  response: responseProp,
  documentSelected,
  paymentType
}) => {
  const { t } = useTranslation('global-bill-payment');
  const [submittedButton, setSubmittedButton] = useState(false);
  const [datePickerType, setDatePickerType] = useState(DatePickerType.SCHEDULE_DATE_LATER);
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { bottomsheetRef, onOpen, onClose } = useBottomsheet();
  const { bottomsheetRef: bottomSheetOTPRef, onOpen: onOpenOTP, onClose: onCloseOTP } = useBottomsheet();
  const bottomSheetDateRef = useRef<BottomSheet>(null);
  const isFocused = useIsFocused();
  const store = useStore();
  const openBottomSheetDatePicker = () => {
    bottomSheetDateRef.current?.expand();
  };

  const closeBottomSheetDatePicker = () => {
    bottomSheetDateRef.current?.close();
  };

  const billPaymentForm = useBillPaymentForm();
  const {
    detailForm: {
      setBankName,
      setBankAccountNumber,
      setAdditionalDetail,
      setAssetName,
      setAmount,
      setWalletId,
      setDebitAccountWallerId,
      setDebitAccountWalletName,
      setInstructionId
    },
    scheduleForm: {
      schedule,
      dateOfFirstPayment,
      paymentEndDate,
      onValidateScheduleForm,
      setSchedule,
      setDateOfFirstPayment,
      setPaymentEndDate,
      setRemarks
    },
    general: { validated, quoteDataRequest, setValidated, setFlagSubmitted, setQuoteDataRequestProps, userFee }
  } = billPaymentForm;
  const createBillPayment = useCreateBillPayment();
  const {
    general: { alertMessage, setAlertMessage, mapSearchWithResponse },
    instruction: { postQuote, isLoadingQuote, mapQuoteDataRequest }
  } = createBillPayment;
  const [dataResult, setDataResult] = useState<BillPaymentConfirmProp>();

  useEffect(() => {
    if (!_.isUndefined(detailFormProp)) {
      setBankName(detailFormProp.bank);
      setBankAccountNumber(detailFormProp.bankAccountNumber);
      setAdditionalDetail(detailFormProp.additionalDetail);
      setAssetName(detailFormProp.assetName);
      setAmount(detailFormProp.amount);
      setWalletId(detailFormProp.walletId);
      setDebitAccountWallerId(detailFormProp.debitAccountWallerId);
      setDebitAccountWalletName(detailFormProp.debitAccountWalletName);
      setRemarks(detailFormProp.remarks);
      setInstructionId(responseProp?.id ?? '');
    }
    setQuoteDataRequestProps(request);
  }, [detailFormProp]);

  const onDateClick = (input: DatePickerType) => {
    setDatePickerType(input);
    openBottomSheetDatePicker();
  };

  const getDateProps = () => {
    switch (datePickerType) {
      case DatePickerType.SCHEDULE_DATE_LATER: {
        return schedule;
      }
      case DatePickerType.DATE_OF_FIRST_PAYMENT: {
        return dateOfFirstPayment;
      }
      case DatePickerType.END_DATE_PAYMENT: {
        return paymentEndDate;
      }
      default:
        return null;
    }
  };

  const onDateChange = (value: Moment | null) => {
    switch (datePickerType) {
      case DatePickerType.SCHEDULE_DATE_LATER: {
        setSchedule(value);
        break;
      }
      case DatePickerType.DATE_OF_FIRST_PAYMENT: {
        setDateOfFirstPayment(value);
        setPaymentEndDate(null);
        break;
      }
      case DatePickerType.END_DATE_PAYMENT: {
        setPaymentEndDate(value);
        break;
      }
      default:
        return null;
    }
  };

  const getMinMaxDateOption = () => {
    switch (datePickerType) {
      case DatePickerType.SCHEDULE_DATE_LATER: {
        return {
          minDate: moment().add('1', 'days')
        };
      }
      case DatePickerType.DATE_OF_FIRST_PAYMENT: {
        return {
          minDate: moment()
        };
      }
      case DatePickerType.END_DATE_PAYMENT: {
        return {
          minDate: dateOfFirstPayment ? dateOfFirstPayment : moment()
        };
      }
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (submittedButton && validated) {
      setSubmittedButton(false);
      setValidated(false);
      setAlertMessage('');

      postQuote(
        request,
        (data) => {
          const response: QuoteResponse = data.data;
          const debitAccount = `${billPaymentForm.detailForm.debitAccountWalletName}\n${billPaymentForm.detailForm.debitAccountWallerId}`;
          setDataResult({
            request: mapQuoteDataRequest(quoteDataRequest, response),
            response: mapSearchWithResponse(response, responseProp),
            flagUploaded: true,
            debitAccount: debitAccount,
            documentSelected: documentSelected,
            paymentType
          });
          setTimeout(() => {
            onOpen();
          }, 400);
        },
        (error) => {
          console.log(error);
          const { data } = error.response || ({} as any);
          const errorMessage = data && _.isString(data.message) ? data.message : error.message;
          setAlertMessage(errorMessage);
        }
      );
    }
    const timeout = setTimeout(() => setSubmittedButton(false), 500);
    return () => clearTimeout(timeout);
  }, [submittedButton, validated]);

  useEffect(() => {
    bottomSheetDateRef.current?.collapse();
    bottomSheetDateRef.current?.close();
  }, []);

  const [initOTP, setInitOTP] = useState(false);

  const onResultConfirm = () => {
    onClose();
    setInitOTP(true);
    setTimeout(() => {
      onOpenOTP();
    }, 500);
  };

  const OTPBottomSheet = () => {
    if (initOTP && !!dataResult) {
      if (store.users && store.users.tfaKeyStatus)
        return (
          <ConfirmGoogleOtp
            bottomsheetRef={bottomSheetOTPRef}
            onClose={onCloseOTP}
            dataProp={dataResult}
            userFee={userFee}
            documentSelected={documentSelected}
          />
        );
      else
        return (
          <ConfirmSmsOtp
            bottomsheetRef={bottomSheetOTPRef}
            onClose={onCloseOTP}
            dataProp={dataResult}
            userFee={userFee}
            documentSelected={documentSelected}
          />
        );
    }
    return <></>;
  };

  const onBack = () => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      onGoBack(navigation);
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      onGoBack(navigation);
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
          setTimeout(() => {
            const popStateLocked = popStateLockedMap.get('popstate') ?? false;
            if (!document.location.href || popStateLocked) return;
            const { parentName, subName } = mapUrlLink(document.location.href);
            var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
            if (
              screenNameObject.screenName &&
              screenNameObject.routeStackName === parentName &&
              screenNameObject.screenName === BillPaymentStackRoutes.FormSchedule
            ) {
              onBack();
            }
          }, 200);
        };
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  return (
    <View sx={sxStyles.container}>
      <View sx={{ ...sxStyles.header, ...{ pt: insets.top } }}>
        <HeaderBackgroundImageComponent />
        <UniTabHeader
          title={t('global-bill-payment::glossary.title--payment-instruction')}
          onBack={() => onBack()}
          labelColor="$white"
          sx={sxStyles.tab}
        />
      </View>
      <ScrollView sx={{ padding: '$3', flex: 1 }} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.content}>
            <ScheduleComponentMobileComponent billPaymentProp={billPaymentForm} onDateClick={onDateClick} />
          </View>
          <View style={{ flex: 1, justifyContent: 'flex-end' }}>
            {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={sxStyles.viewAlert} />}
            <UniButton
              label={t('global-bill-payment::glossary.submit')}
              onPress={() => {
                setFlagSubmitted(true);
                setSubmittedButton(true);
                setInitOTP(false);
                onValidateScheduleForm();
              }}
              sx={sxStyles.button}
              size="medium"
              disabled={isLoadingQuote}
              loading={isLoadingQuote}
            />
          </View>
        </View>
      </ScrollView>
      {isFocused && (
        <>
          <ConfirmFormBillPayment bottomsheetRef={bottomsheetRef} onClose={onClose} dataProp={dataResult} onResult={onResultConfirm} />
          {OTPBottomSheet()}
          <UniBottomSheet bottomSheetRef={bottomSheetDateRef} enableTouchThrough={false} initialPointIndex={-1}>
            <DateFilterPopup
              closeBottomSheet={closeBottomSheetDatePicker}
              dateProps={getDateProps()}
              onValueChange={onDateChange}
              options={getMinMaxDateOption()}
            />
          </UniBottomSheet>
        </>
      )}
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    width: '100%',
    height: '100%',
    flex: 1
  },
  body: {
    flex: 1,
    justifyItems: 'space-between'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  header: {
    flexDirection: 'column'
  },
  content: {
    flexDirection: 'column',
    zIndex: 2
  },
  button: {
    width: '100%',
    height: '$11'
  },
  viewAlert: {
    mb: '$3'
  }
};
